import React from 'react';

import {Text} from '@albert-io/atomic';

import {SortableHeaderCell} from './SortableHeaderCell';
import type {SortDir} from './useClientSort';

export interface RosterColumn<Row> {
  /** Stable column id; for sortable columns it is also the sort key passed to onSort/useClientSort. */
  key: string;
  /** Header label; a sortable column renders a SortableHeaderCell with it. */
  label?: string;
  sortable?: boolean;
  /** Classes for the non-sortable <th> (e.g. a fixed width for the actions column). */
  headerClassName?: string;
  /** Visually-hidden header text for icon-only columns (e.g. "Actions"). */
  srOnlyHeader?: string;
  /** Classes for each body <td>. */
  cellClassName?: string;
  render: (row: Row) => React.ReactNode;
}

interface Props<Row> {
  ariaLabel: string;
  columns: RosterColumn<Row>[];
  rows: Row[];
  getRowKey: (row: Row) => string;
  sortKey: string;
  sortDir: SortDir;
  onSort: (key: string) => void;
  noResultsMessage: string;
}

/**
 * Shared roster table for the People tab's three lists (enrolled / invited / co-teachers): the table
 * chrome, sortable headers (SortableHeaderCell), and the no-search-results message. Each column
 * carries its own header/cell classes and a render fn, so every list keeps its exact markup.
 */
export function RosterTable<Row>({
  ariaLabel,
  columns,
  rows,
  getRowKey,
  sortKey,
  sortDir,
  onSort,
  noResultsMessage
}: Props<Row>) {
  return (
    <div>
      <table aria-label={ariaLabel} className='w-full border-collapse text-s'>
        <thead>
          <tr className='border-b border-border-primary'>
            {columns.map((column) =>
              column.sortable && column.label ? (
                <SortableHeaderCell
                  activeKey={sortKey}
                  dir={sortDir}
                  key={column.key}
                  label={column.label}
                  onSort={onSort}
                  sortKey={column.key}
                />
              ) : (
                <th className={column.headerClassName} key={column.key}>
                  {column.srOnlyHeader ? (
                    <span className='sr-only'>{column.srOnlyHeader}</span>
                  ) : null}
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr className='border-b border-border-subtle' key={getRowKey(row)}>
              {columns.map((column) => (
                <td className={column.cellClassName} key={column.key}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <Text as='p' className='py-x4 text-center' color='secondary'>
          {noResultsMessage}
        </Text>
      )}
    </div>
  );
}
